import type { ReactNode } from 'react';

/**
 * One number and what it means.
 *
 * `tone` is used only where a number is genuinely good or bad — a breached SLA
 * count, an overdue queue. Colouring every tile would make none of them mean
 * anything, which is how dashboards become wallpaper.
 */
export function StatTile({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: ReactNode;
  hint?: string | undefined;
  tone?: 'neutral' | 'good' | 'warn' | 'bad';
}) {
  const toneClass = {
    neutral: 'text-slate-900',
    good: 'text-green-700',
    warn: 'text-amber-700',
    bad: 'text-red-700',
  }[tone];

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className={`text-2xl font-bold tabular-nums ${toneClass}`} dir="ltr">
        {value}
      </div>
      <div className="mt-1 text-xs font-medium text-slate-600">{label}</div>
      {hint ? <div className="mt-0.5 text-xs text-slate-400">{hint}</div> : null}
    </div>
  );
}

/** A horizontal bar, for the funnel and target attainment. No chart library: one
 *  div is cheaper than 60 KB of JavaScript on a 3G phone. */
export function Bar({
  value,
  max,
  tone = 'brand',
}: {
  value: number;
  max: number;
  tone?: 'brand' | 'good' | 'warn' | 'bad';
}) {
  const pct = max <= 0 ? 0 : Math.min(100, Math.round((value / max) * 100));
  const colour = {
    brand: 'bg-brand-600',
    good: 'bg-green-500',
    warn: 'bg-amber-400',
    bad: 'bg-red-500',
  }[tone];

  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
      <div className={`h-full rounded-full ${colour}`} style={{ width: `${pct}%` }} />
    </div>
  );
}
